import { API_HOST, STORAGE_KEY } from "@/lib/config"

const TRPC_BASE = `${API_HOST}/trpc`

function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {}
  const token = localStorage.getItem(STORAGE_KEY)
  if (token) {
    ;(headers as Record<string, string>).Authorization = `Bearer ${token}`
  }
  return headers
}

async function tRPCRequest<T>(
  procedure: string,
  input?: unknown,
  method?: "GET" | "POST",
): Promise<T> {
  const token = localStorage.getItem(STORAGE_KEY)
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  }
  if (token) {
    headers["Authorization"] = `Bearer ${token}`
  }

  let url = `${TRPC_BASE}/${procedure}`
  const fetchOptions: RequestInit = { headers }

  if (method === "GET" || (method === undefined && input === undefined)) {
    fetchOptions.method = "GET"
    if (input !== undefined) {
      url += `?input=${encodeURIComponent(JSON.stringify(input))}`
    }
  } else {
    fetchOptions.method = "POST"
    if (input !== undefined) {
      fetchOptions.body = JSON.stringify(input)
    }
  }

  const response = await fetch(url, fetchOptions)
  const data = await response.json()

  if (!response.ok) {
    const errorMessage = data?.error?.message || "请求失败"
    try {
      const parsed = JSON.parse(errorMessage)
      if (Array.isArray(parsed) && parsed[0]?.message) {
        throw new Error(parsed.map((e: any) => e.message).join(", "))
      }
    } catch (e) {
      // Not JSON, use as-is
    }
    throw new Error(errorMessage)
  }

  return data.result.data as T
}

export interface ExamPaperUpdate {
  name?: string | null
  exam_type?: string | null
  category?: string | null
  subject?: string | null
  grade?: string | null
  stage?: string | null
  year?: number | null
  province?: string | null
  city?: string | null
  source_book?: string | null
  chapter?: string | null
  total_score?: number | null
  estimated_duration_minutes?: number | null
  difficulty_level?: number | null
  tags?: Record<string, any> | null
}

export interface ExamPaperListItem {
  id: string
  name: string
  exam_type?: string | null
  category?: string | null
  subject?: string | null
  grade?: string | null
  stage?: string | null
  year?: number | null
  province?: string | null
  city?: string | null
  competition_code?: string | null
  competition_year?: number | null
  source_book?: string | null
  chapter?: string | null
  total_score?: number | null
  estimated_duration_minutes?: number | null
  difficulty_level?: number | null
  tags?: Record<string, any> | null
  created_by?: number | null
  question_count: number
}

export interface ExamPaperQuestionItem {
  question_id: string
  index_in_paper: number
  section?: string | null
  score?: number | null
  type?: string | null
  difficulty?: number | null
  knowledge_points?: string[] | null
  content?: { stem?: string; [key: string]: any }
  options?: { label?: string; content?: string; [key: string]: any }[] | null
  answer?: { value?: string; [key: string]: any } | null
  // Group fields
  group_id?: string | null
  group_type?: string | null
  passage_content?: string | null
  group_images?: string[] | null
  group_options?: { label: string; content: string }[] | null
}

export interface ExamPaperDetail {
  id: string
  name: string
  exam_type?: string | null
  category?: string | null
  competition_code?: string | null
  competition_year?: number | null
  subject?: string | null
  grade?: string | null
  stage?: string | null
  year?: number | null
  province?: string | null
  city?: string | null
  source_book?: string | null
  chapter?: string | null
  total_score?: number | null
  estimated_duration_minutes?: number | null
  difficulty_level?: number | null
  tags?: Record<string, any> | null
  created_by?: number | null
  questions: ExamPaperQuestionItem[]
}

export interface ExamPaperQuestionInsertRequest {
  insert_after_question_id?: string | null
  type?: string | null
}

export interface ExamPaperQuestionInsertResponse {
  paper: ExamPaperDetail
  inserted_question_id: string
}

// 考试会话模式：practice=普通练习，full=整套模拟，test=测试模式（作答中禁止讲解/批改），offline=线下作答（下载试卷纸笔作答后上传照片）
export type ExamSessionMode = 'full' | 'practice' | 'test' | 'offline'

export interface ExamSessionInfo {
  id: string
  paper_id: string
  mode: string
  status: string
  selected_question_ids: string[]
}

export interface ExamSessionDetail {
  id: string
  paper: ExamPaperDetail
  mode: string
  status: string
  selected_question_ids: string[]
}

export interface ExamQuestionReportItem {
  question_id: string
  index_in_paper: number
  section?: string | null
  score?: number | null
  user_answer?: Record<string, any> | null
  is_correct?: boolean | null
  score_obtained?: number | null
  type?: string | null
  difficulty?: number | null
  knowledge_points?: string[] | null
  content?: Record<string, any> | null
  options?:
    | { label?: string; content?: string; images?: string[]; [key: string]: any }[]
    | null
  analysis?: string | null
  feedback?: string | null
  correct_answer?: string | null
  is_marked?: boolean
  subject?: string | null
  // Group fields (for composite English questions)
  group_id?: string | null
  group_type?: string | null
  passage_content?: string | null
  audio_url?: string | null
  group_images?: string[] | null
  group_options?: { label: string; content: string }[] | null
}

export interface ExamSessionReport {
  session_id: string
  paper: ExamPaperDetail
  total_score?: number | null
  correct_count?: number | null
  wrong_count?: number | null
  unanswered_count?: number | null
  questions: ExamQuestionReportItem[]
}

export interface ExamGradeRequest {
  sessionId: string
  questionId: string
  imageUrl?: string
  userAnswer?: Record<string, unknown>
  // submit 表示提交试卷时的统一批改，测试模式下仅允许该场景
  purpose?: 'interactive' | 'submit'
}

export interface ExamGradeResponse {
  questionId: string
  score?: number
  maxScore?: number
  isCorrect: boolean
  feedback: string
  analysis?: string | null
  pointsDeducted?: any[] | null
  correctAnswer?: string | null
}

export interface ExamSessionProgress {
  answerMap?: Record<string, any> | null
  answerModeMap?: Record<string, "text" | "handwriting" | "upload"> | null
  currentIndex?: number | null
  updatedAt?: string | null
}

export interface ExamSessionListItem {
  id: string
  paper: ExamPaperDetail
  mode: string
  status: string
  started_at: string
  submitted_at?: string | null
  total_score?: number | null
  progress?: {
    current_index?: number
    answer_map?: Record<string, any>
  } | null
}

// Paper CRUD
export async function fetchExamPapers(): Promise<ExamPaperListItem[]> {
  return tRPCRequest<ExamPaperListItem[]>("exam.listPapers", undefined, "GET")
}

export async function fetchExamPaperDetail(
  paperId: string,
): Promise<ExamPaperDetail> {
  return tRPCRequest<ExamPaperDetail>("exam.getPaper", { paperId }, "GET")
}

export async function createExamPaper(
  payload: ExamPaperUpdate,
): Promise<ExamPaperDetail> {
  return tRPCRequest<ExamPaperDetail>("exam.createPaper", payload)
}

export async function updateExamPaper(
  paperId: string,
  payload: ExamPaperUpdate,
): Promise<ExamPaperDetail> {
  return tRPCRequest<ExamPaperDetail>("exam.updatePaper", {
    paperId,
    ...payload,
  })
}

export async function deleteExamPaper(paperId: string): Promise<void> {
  return tRPCRequest<void>("exam.deletePaper", { paperId })
}

export async function insertExamPaperQuestion(
  paperId: string,
  payload: ExamPaperQuestionInsertRequest,
): Promise<ExamPaperQuestionInsertResponse> {
  return tRPCRequest<ExamPaperQuestionInsertResponse>("exam.addQuestions", {
    paperId,
    ...payload,
  })
}

export async function deleteExamPaperQuestion(
  paperId: string,
  questionId: string,
): Promise<{ success: boolean }> {
  return tRPCRequest<{ success: boolean }>("exam.deleteQuestion", { paperId, questionId })
}

export async function updateExamPaperQuestion(
  questionId: string,
  payload: {
    content?: { stem?: string; images?: string[]; [key: string]: any }
    type?: string
    options?: { label: string; content: string }[] | null
    answer?: { value: string }
    analysis?: string
    difficulty?: number
  },
): Promise<{ success: boolean }> {
  return tRPCRequest<{ success: boolean }>("exam.updateQuestionContent", {
    questionId,
    ...payload,
  })
}

export async function updateExamGroup(
  groupId: string,
  payload: {
    type?: string
    passage_content?: string
    options?: { label: string; content: string }[] | null
  },
): Promise<{ success: boolean }> {
  return tRPCRequest<{ success: boolean }>("exam.updateGroup", {
    groupId,
    ...payload,
  })
}

// Session lifecycle
export async function fetchExamSessions(params?: {
  paperId?: string
  status?: string
}): Promise<ExamSessionListItem[]> {
  return tRPCRequest<ExamSessionListItem[]>("exam.listSessions", params, "GET")
}

export async function createExamSession(
  paperId: string,
  mode: ExamSessionMode = "practice",
  forceNew: boolean = false,
): Promise<ExamSessionInfo> {
  return tRPCRequest<ExamSessionInfo>("exam.createSession", {
    paperId,
    mode,
    forceNew,
  })
}

export async function fetchExamSessionDetail(
  sessionId: string,
): Promise<ExamSessionDetail> {
  return tRPCRequest<ExamSessionDetail>("exam.getSession", { sessionId }, "GET")
}

export async function submitExamAnswer(
  sessionId: string,
  payload: { questionId: string; userAnswer: any },
): Promise<void> {
  return tRPCRequest<void>("exam.saveAnswer", { sessionId, ...payload })
}

export interface KpBreakdownItem {
  kpCode: string
  name: string
  total: number
  correct: number
  accuracy: number
  severity: 'high' | 'medium' | 'low'
}

export interface KnowledgeSummary {
  totalQuestions: number
  untaggedCount: number
  kpBreakdown: KpBreakdownItem[]
  weakPoints: KpBreakdownItem[]
}

export async function submitExamSession(sessionId: string): Promise<{
  session_id: string
  total_score?: number
  correct_count?: number
  wrong_count?: number
  unanswered_count?: number
  knowledgeSummary?: KnowledgeSummary
}> {
  return tRPCRequest("exam.submitSession", { sessionId })
}

export interface ExamDiagnosisResponse {
  session: {
    totalScore: number | null
    correctCount: number | null
    wrongCount: number | null
    unansweredCount: number | null
  }
  knowledgeSummary: KnowledgeSummary | null
  diagnosis: {
    knowledgeSummary?: KnowledgeSummary
    mistakeAnalysis?: any
    learningPlan?: any
    questionTypeBreakdown?: Array<{
      type: string
      name: string
      total: number
      correct: number
      accuracy: number
    }>
    abilityProfile?: Record<string, { level: string; score: number; description: string }>
    errorAnnotations?: Array<{ questionId: string; errorReason: string; detail: string }>
    overallSummary?: string
  } | null
  diagnosisId: string | null
  courses: Array<{
    id: string
    title: string
    courseType: string
    status: string
    totalLessons: number
    completedLessons: number
  }>
}

export async function getExamDiagnosis(sessionId: string): Promise<ExamDiagnosisResponse> {
  return tRPCRequest("aiTeam.getExamDiagnosis", { sessionId }, "GET")
}

export async function fetchExamSessionReport(
  sessionId: string,
): Promise<ExamSessionReport> {
  return tRPCRequest<ExamSessionReport>(
    "exam.getSessionReport",
    { sessionId },
    "GET",
  )
}

export async function fetchExamSessionProgress(
  sessionId: string,
): Promise<ExamSessionProgress> {
  return tRPCRequest<ExamSessionProgress>(
    "exam.getProgress",
    { sessionId },
    "GET",
  )
}

export async function saveExamSessionProgress(
  sessionId: string,
  payload: ExamSessionProgress,
): Promise<ExamSessionProgress> {
  return tRPCRequest<ExamSessionProgress>("exam.saveProgress", {
    sessionId,
    ...payload,
  })
}

export async function gradeQuestion(
  data: ExamGradeRequest,
): Promise<ExamGradeResponse> {
  return tRPCRequest<ExamGradeResponse>("exam.gradeQuestion", data)
}

// ─── 线下作答 ─────────────────────────────

export interface OfflineSplitResultItem {
  questionId: string
  indexInPaper: number
  type: string | null
  extractedAnswer: string
  matched: boolean
}

export interface OfflineSplitResponse {
  sessionId: string
  totalQuestions: number
  recognizedCount: number
  results: OfflineSplitResultItem[]
}

/**
 * 上传整张作答纸照片，由 AI 按题号识别切分每题作答并写入会话。
 * imageUrls 为已上传到服务器的图片可访问 URL（1-3 张）。
 */
export async function splitOfflineSheet(
  sessionId: string,
  imageUrls: string[],
): Promise<OfflineSplitResponse> {
  return tRPCRequest<OfflineSplitResponse>("exam.splitOfflineSheet", {
    sessionId,
    imageUrls,
  })
}

/**
 * 在新窗口打开试卷可打印 HTML（浏览器打印为 PDF）
 */
export async function openExamPaperPdf(
  paperId: string,
  withAnswers: boolean,
): Promise<void> {
  const token = localStorage.getItem(STORAGE_KEY)
  const url = `${API_HOST}/api/v1/exam/papers/${paperId}/print?withAnswers=${withAnswers ? 'true' : 'false'}`

  const response = await fetch(url, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error((data as any)?.error || '导出失败')
  }

  const html = await response.text()
  const printWindow = window.open('', '_blank')
  if (!printWindow) {
    throw new Error('无法打开新窗口，请检查浏览器是否阻止了弹出窗口')
  }
  // Inject <base> so relative image URLs (/media/...) resolve in the new window
  const htmlWithBase = html.replace('<head>', `<head><base href="${window.location.origin}">`)
  printWindow.document.write(htmlWithBase)
  printWindow.document.close()
}

export async function onExamSubmit(sessionId: string): Promise<{
  diagnosisId: string
  mistakeAnalysis: any
  learningPlan: any
}> {
  return tRPCRequest("aiTeam.onExamSubmit", { sessionId })
}
